import React, { useState, useRef } from 'react';
import styled from '@emotion/styled';
import { createPortal } from 'react-dom';
import {
  BarChart,
  Bar,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import useWindowDimensions from 'ui_system/utilities/useWindowDimensions';
import { colors, shadows, shape, spacing } from '../utils';
import {
  ChartWrapper,
  OverflowContainer,
  StickyXAxis,
  StickyYAxis,
  Watermark,
} from '../chart_styles/styles';

const TooltipWrapper = styled.div`
  background: ${colors.white};
  box-shadow: ${shadows.primary};
  font-family: Soleil;
  font-size: 13px;
  left: ${(props) => props.x}px;
  padding: ${spacing.medium};
  position: absolute;
  top: ${(props) => props.y}px;
  z-index: 100;
`;

const TooltipLabel = styled.p`
  color: ${colors.text.secondary};
  font-family: Soleil;
  font-size: 11px;
  font-weight: 400;
  margin: 0;
  text-transform: uppercase;
`;

const TooltipLegend = styled.div`
  display: flex;
  flex-direction: column;
`;

const TooltipLegendItem = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: ${spacing.xxsmall};
`;

const TooltipLegendSquare = styled.div`
  background: ${(props) => props.color};
  border-radius: ${shape.borderRadius};
  height: ${spacing.medium};
  margin-right: ${spacing.xsmall};
  width: ${spacing.medium};
`;

const TooltipLegendSquareLabel = styled.div`
  align-items: center;
  display: flex;
  padding-right: ${spacing.medium};
`;

const TooltipLegendValue = styled.div`
  display: flex;
  font-weight: 600;
`;

const TooltipValue = styled.p`
  color: ${colors.text.primary};
  font-weight: 600;
  margin: 0;
  margin-bottom: ${spacing.medium};
`;

export const CustomTooltip = (props) => {
  const { active, coordinates, dimensions, formatter, label, name, payload } =
    props;
  const tooltipRef = useRef(null);
  let tooltipWidth;
  const target = document.body;
  if (!active || !coordinates) return null;
  if (tooltipRef.current) {
    tooltipWidth = tooltipRef.current.offsetWidth;
  }

  const { width } = dimensions;
  let x = coordinates[0] + 10;
  const y = coordinates[1];
  if (x + tooltipWidth >= width) {
    x -= tooltipWidth + 20;
  }

  const TooltipPortal = (
    <TooltipWrapper ref={tooltipRef} x={x} y={y}>
      {name && (
        <>
          <TooltipLabel>{name}</TooltipLabel>
          <TooltipValue>{label}</TooltipValue>
        </>
      )}
      <TooltipLabel>{payload[0].unit}</TooltipLabel>
      <TooltipValue>
        {formatter(payload[0].payload.totalValue, true)[0]}
      </TooltipValue>
      <TooltipLegend>
        {payload.map((item) => (
          <TooltipLegendItem key={item.name}>
            <TooltipLegendSquareLabel>
              <TooltipLegendSquare color={item.fill} />
              {item.name}
            </TooltipLegendSquareLabel>
            <TooltipLegendValue>{formatter(item.value)[0]}</TooltipLegendValue>
          </TooltipLegendItem>
        ))}
      </TooltipLegend>
    </TooltipWrapper>
  );

  return createPortal(TooltipPortal, target);
};

export const StackedBarChart = ({
  barCategoryGap = 12,
  barGap = 12,
  barName = '',
  barSize,
  data = [],
  dataKey,
  disableAnimations = false,
  downloadRef,
  height = 400,
  includeWatermark = false,
  layout = 'vertical',
  legendType = 'none',
  margins,
  maxBarSize = 60,
  maxHeight,
  maxWidth,
  minWidth,
  stackOrder,
  stackColors,
  stickyAxes = false,
  tooltipFormatter,
  totalValue,
  unit,
  xAxisProps = {},
  yAxisProps = {},
}) => {
  const dimensions = useWindowDimensions();
  const containerRef = useRef(null);
  const [mouseCoordinates, setMouseCoordinates] = useState(null);
  let maxChartWidth = maxWidth;
  let maxChartHeight = maxHeight;
  let minChartWidth = minWidth + (yAxisProps?.width || 60); // 60 is default yAxis width
  const chartMargins = { bottom: 35, left: 25, top: 10, right: 50, ...margins };
  let hideXAxis = !!xAxisProps.hide;
  let yAxisLabel = yAxisProps?.label;
  const animationTime = 1000 / stackOrder.length;

  if (stickyAxes) {
    hideXAxis = true;
    yAxisLabel = false;
    chartMargins.bottom = 0;
    maxChartWidth -= 50;
    minChartWidth -= 50;
    maxChartHeight -= 70;
  }
  const stackedBars = stackOrder.map((barKey, index) => (
    <Bar
      animationBegin={index * animationTime}
      animationDuration={animationTime}
      barSize={barSize}
      dataKey={barKey}
      fill={stackColors[index]}
      id={barKey}
      isAnimationActive={!disableAnimations}
      key={barKey}
      legendType={legendType}
      maxBarSize={maxBarSize}
      name={barKey}
      stackId="stackedbarchart"
      unit={unit}
    />
  ));

  const onMouseMove = (_, e) => {
    setMouseCoordinates([e.clientX, e.clientY]);
  };

  return (
    <>
      {stickyAxes && (
        <StickyYAxis label={yAxisProps?.label?.value} maxHeight={maxHeight} />
      )}
      <ChartWrapper maxWidth={maxChartWidth} minWidth={minChartWidth}>
        <OverflowContainer
          maxHeight={maxChartHeight}
          maxWidth={maxChartWidth}
          minWidth={minChartWidth}
        >
          {includeWatermark && <Watermark />}
          <ResponsiveContainer
            height={height}
            minWidth={minChartWidth}
            ref={containerRef}
            width="100%"
          >
            <BarChart
              barCategoryGap={barCategoryGap}
              barGap={barGap}
              barSize={barSize}
              data={data}
              height={height}
              layout={layout}
              margin={chartMargins}
              onMouseMove={onMouseMove}
              ref={downloadRef}
            >
              <CartesianGrid
                horizontal={layout === 'horizontal'}
                stroke={colors.graphLine}
                vertical={layout === 'vertical'}
              />
              <XAxis {...xAxisProps} hide={hideXAxis} />
              <YAxis {...yAxisProps} label={yAxisLabel} />
              <Tooltip
                containerRef={containerRef}
                content={
                  <CustomTooltip name={barName} totalValue={totalValue} />
                }
                coordinates={mouseCoordinates}
                cursor={false}
                dimensions={dimensions}
                formatter={tooltipFormatter}
              />
              {stackedBars}
            </BarChart>
          </ResponsiveContainer>
        </OverflowContainer>
        {stickyAxes && (
          <StickyXAxis
            barName={barName}
            data={data}
            dataKey={dataKey}
            layout={layout}
            maxWidth={maxChartWidth}
            minWidth={minChartWidth}
            stackOrder={stackOrder}
            xAxis={{ ...xAxisProps, hide: false }}
            yAxis={{ ...yAxisProps, label: false }}
          />
        )}
      </ChartWrapper>
    </>
  );
};

StackedBarChart.displayName = 'StackedBarChart';
